import { Component, type ReactNode } from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
    this.props.onError?.(error, errorInfo)
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
  }

  handleReload = () => {
    window.location.reload()
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="flex-1 flex items-center justify-center themed-bg-primary p-8">
          <div className="max-w-md text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full themed-bg-secondary flex items-center justify-center">
              <AlertTriangle size={32} className="text-amber-500" />
            </div>
            <h2 className="text-xl font-semibold themed-text mb-2">
              Something went wrong
            </h2>
            <p className="themed-text-muted mb-6">
              An unexpected error occurred. You can try again or reload the page.
            </p>
            {this.state.error && (
              <details className="mb-6 text-left">
                <summary className="themed-text-secondary text-sm cursor-pointer hover:themed-text">
                  Error details
                </summary>
                <pre className="mt-2 p-3 themed-bg-secondary rounded-lg text-xs themed-text-muted overflow-auto max-h-32">
                  {this.state.error.message}
                </pre>
              </details>
            )}
            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleReset}
                className="flex items-center gap-2 px-4 py-2 themed-btn-secondary rounded-lg text-sm font-medium transition-colors"
              >
                <RefreshCw size={16} />
                Try again
              </button>
              <button
                onClick={this.handleReload}
                className="px-4 py-2 themed-btn-primary rounded-lg text-sm font-medium transition-colors"
              >
                Reload page
              </button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
